import React from 'react'
import { LANGUAGES } from '../constants'
import { SUPPORTED_LANGUAGES } from '../i18n'
import { Dispatch, State } from '../store'
import { isPersonal } from '../models/plans'
import { Typography, List } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { DeleteAccountSection } from '../components/DeleteAccountSection'
import { SelectSetting } from '../components/SelectSetting'
import { FormDisplay } from '../components/FormDisplay'
import { Timestamp } from '../components/Timestamp'
import { Container } from '../components/Container'
import { Gutters } from '../components/Gutters'
import { Avatar } from '../components/Avatar'
import { Title } from '../components/Title'
import { Icon } from '../components/Icon'
import { Link } from '../components/Link'
import { spacing } from '../styling'

export const ProfilePage: React.FC = () => {
  const { paidPlan, user, deleteAccount } = useSelector((state: State) => ({
    user: state.user,
    paidPlan: !isPersonal(state),
    deleteAccount: state.ui.deleteAccount,
  }))
  const dispatch = useDispatch<Dispatch>()
  const { t } = useTranslation()

  if (!user) return null

  return (
    <Container
      gutterBottom
      header={
        <>
          <Typography variant="h1">
            <Title>{t('settings.profile', 'Profile')}</Title>
          </Typography>
        </>
      }
    >
      <Gutters
        top="xl"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: 500,
          '& .MuiAvatar-root': { marginRight: `${spacing.xl}px` },
        }}
      >
        <Avatar email={user.email} size={125} />
        <Typography variant="caption">
          {t('settings.gravatarImported', 'Your profile picture is imported from the free service Gravatar.')}
          <br /> {t('settings.gravatarEdit', 'To edit or add a profile image please visit')}{' '}
          <Link href="https://gravatar.com">gravatar.com.</Link>
        </Typography>
      </Gutters>
      <List>
        <FormDisplay icon={<Icon name="at" />} label={t('settings.email', 'Email')} displayValue={user.email} displayOnly />
        <FormDisplay
          icon={<Icon name="calendar-star" />}
          label={t('settings.memberSince', 'Member since')}
          displayValue={<Timestamp date={user.created} />}
          displayOnly
        />
        <SelectSetting
          icon="language"
          label={t('options.language.label', 'Language')}
          value={user?.attributes?.language || 'system'}
          values={[
            { key: 'system', name: t('options.language.system', 'Same as system') },
            ...SUPPORTED_LANGUAGES.map(l => ({ key: l.value, name: l.label })),
          ]}
          onChange={value => dispatch.user.setAppLanguage(value)}
        />
        <SelectSetting
          icon="envelope"
          label={t('settings.emailLanguage', 'Email Language')}
          value={user?.language}
          values={[
            { key: 'en', name: LANGUAGES.en },
            { key: 'ja', name: LANGUAGES.ja },
          ]}
          onChange={value => dispatch.user.changeLanguage(value)}
        />
      </List>
      <Typography variant="subtitle1">{t('settings.accountDeletion', 'Account deletion')}</Typography>
      <DeleteAccountSection user={user} paidPlan={paidPlan} deleteAccount={deleteAccount} />
    </Container>
  )
}

